/**
 * Shape guards for the monitoring truth payloads [S4].
 *
 * A 200 response is not proof of a usable answer. Before these guards the hooks
 * passed any JSON through as typed data, so a malformed body either crashed the
 * whole root in render (there is no ErrorBoundary: `reduce` over `{}` throws) or
 * was read as data (`'1' + 0` concatenates to `'01'`; a missing `tripped` reads
 * as armed). `expectShape` in `hooks.ts` runs one of these on every answer and
 * turns a rejection into a non-retryable `bad_response` error, so React Query
 * keeps the last good data, reports the failure at once, and every consumer
 * fails closed through its error path.
 *
 * Each guard returns `null` when the payload is acceptable, or the reason it is
 * not. The guards check only the fields the safety readings depend on; extra
 * fields are ignored so an additive backend change is never a failure.
 *
 * Pure and runnable under `node --test`: every import is type-only, so nothing
 * here loads the API client (it reads `import.meta.env` when it loads).
 */
import type { ReconciliationDataState } from '@/components/control/types';

import type {
  BookEnvironment,
  BookOverview,
  EngineCitySafetyOut,
  Environment,
  LedgerScope,
  MarkState,
  MonitoringCoinSide,
  MonitoringDeploymentRow,
  MonitoringEnvironmentTotal,
  MonitoringRunnerCounts,
  OpenRiskState,
} from './types';

/** `null` when acceptable, otherwise the reason the payload was refused. */
export type ShapeGuard = (value: unknown) => string | null;

type JsonRecord = Record<string, unknown>;

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function describe(value: unknown): string {
  if (value === undefined) return 'missing';
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'an array';
  if (typeof value === 'string') return `the string ${JSON.stringify(value.slice(0, 40))}`;
  if (typeof value === 'number') return `the number ${String(value)}`;
  return `a ${typeof value}`;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isSafeCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

/**
 * A money string as the backend writes it: `str(Decimal)` [RV-1].
 *
 * Plain decimals (`'10000.000000000000'`, `'-2.5'`) and the exponent forms
 * Python's `Decimal` produces for a zero or a tiny value (`'0E-12'` for a stop
 * trailed past entry or a flat account, `'-1.00000E-7'`) are valid money; a
 * plain-decimal pattern would fail the whole Overview, Portfolio and the
 * Dashboard on normal data. `NaN`, `Infinity`, an overflow (`'1e309'`), hex,
 * padding, grouping commas and the empty string are not.
 */
export function isWireDecimal(value: unknown): value is string {
  return typeof value === 'string'
    && /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(value)
    && Number.isFinite(Number(value));
}

/** A member of a closed string union, checked against an exhaustive table. */
function isMember<K extends string>(table: Record<K, true>, value: unknown): value is K {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(table, value);
}

// Exhaustive tables: TypeScript rejects a missing or an extra member, so a change
// to the wire union breaks the build instead of silently widening the guard.
const SAFETY_LEVELS: Record<EngineCitySafetyOut['level'], true> = {
  safe: true,
  warning: true,
  blocked: true,
};
const PROTECTION_STATES: Record<MonitoringDeploymentRow['protection_state'], true> = {
  flat: true,
  verified: true,
  unprotected: true,
  unverified: true,
};
const RECONCILIATION_DATA_STATES: Record<ReconciliationDataState, true> = {
  uninitialised: true,
  not_run: true,
  stale: true,
  live: true,
};
const SNAPSHOT_STATES: Record<MonitoringCoinSide['snapshot_state'], true> = {
  fresh: true,
  stale: true,
  missing: true,
};
const OPEN_RISK_STATES: Record<OpenRiskState, true> = { none: true, bounded: true, unbounded: true };
const LEDGER_SCOPES: Record<LedgerScope, true> = { exchange: true, paper_untracked: true };
const ENVIRONMENTS: Record<Environment, true> = { paper: true, testnet: true, live: true };
const MONEY_SOURCES: Record<MonitoringEnvironmentTotal['money_source'], true> = {
  portfolio_book: true,
  not_measured: true,
};
const BOOK_STATES: Record<BookOverview['state'], true> = { ok: true, no_account: true };
const BOOK_ENVIRONMENTS: Record<BookEnvironment, true> = { testnet: true, live: true };
const MARK_STATES: Record<MarkState, true> = { fresh: true, stale: true, unavailable: true };
/** Every count a runner tally carries; `terminated` since M2-S1. */
const RUNNER_COUNT_FIELDS: readonly (keyof MonitoringRunnerCounts)[] = [
  'total', 'terminated', 'attached', 'attaching', 'pending', 'failed', 'stale', 'detached', 'blocked_live',
  'legacy_review',
];

function checkString(body: JsonRecord, field: string): string | null {
  return typeof body[field] === 'string' ? null : `${field} is ${describe(body[field])}, not a string`;
}

function checkBoolean(body: JsonRecord, field: string): string | null {
  return typeof body[field] === 'boolean' ? null : `${field} is ${describe(body[field])}, not a boolean`;
}

function firstFailure(...checks: (string | null)[]): string | null {
  for (const check of checks) if (check !== null) return check;
  return null;
}

/** `GET /monitoring/engine-city/safety`: the entry verdict. */
export const safetyVerdictShape: ShapeGuard = (value) => {
  if (!isRecord(value)) return `the answer is ${describe(value)}, not an object`;
  return firstFailure(
    isMember(SAFETY_LEVELS, value.level) ? null : `level is ${describe(value.level)}, not safe, warning or blocked`,
    checkString(value, 'headline'),
    checkString(value, 'detail'),
    checkString(value, 'reason'),
    value.until_ms === null || isFiniteNumber(value.until_ms)
      ? null
      : `until_ms is ${describe(value.until_ms)}, not a finite number or null`,
  );
};

/** `GET /system/killswitch`: only a boolean `tripped` is a reading of the breaker. */
export const killSwitchShape: ShapeGuard = (value) => {
  if (!isRecord(value)) return `the answer is ${describe(value)}, not an object`;
  return checkBoolean(value, 'tripped');
};

/** `GET /monitoring/deployments` (both query keys): the protection rows. */
export const deploymentRowsShape: ShapeGuard = (value) => {
  if (!Array.isArray(value)) return `the answer is ${describe(value)}, not an array of rows`;
  for (let index = 0; index < value.length; index += 1) {
    const row: unknown = value[index];
    if (!isRecord(row)) return `row ${index} is ${describe(row)}, not an object`;
    const failure = firstFailure(
      typeof row.deployment_id === 'number' && Number.isSafeInteger(row.deployment_id)
        ? null
        : `deployment_id is ${describe(row.deployment_id)}, not an integer`,
      checkString(row, 'symbol'),
      isSafeCount(row.unprotected_positions)
        ? null
        : `unprotected_positions is ${describe(row.unprotected_positions)}, not a whole number of 0 or more`,
      isMember(PROTECTION_STATES, row.protection_state)
        ? null
        : `protection_state is ${describe(row.protection_state)}, not flat, verified, unprotected or unverified`,
    );
    if (failure !== null) return `row ${index}: ${failure}`;
  }
  return null;
};

/** A money field: a wire decimal or null (unknown), never a guessed number. */
function checkMoney(body: JsonRecord, field: string): string | null {
  const value = body[field];
  return value === null || isWireDecimal(value) ? null : `${field} is ${describe(value)}, not a decimal string or null`;
}

/** A composed P&L object whose `net_pnl` is a wire decimal or null. */
function checkPnl(body: JsonRecord, field: string): string | null {
  const pnl = body[field];
  if (!isRecord(pnl)) return `${field} is ${describe(pnl)}, not an object`;
  const net = checkMoney(pnl, 'net_pnl');
  return net === null ? null : `${field}.${net}`;
}

function checkRunnerCounts(value: unknown, where: string): string | null {
  if (!isRecord(value)) return `${where} is ${describe(value)}, not an object`;
  for (const field of RUNNER_COUNT_FIELDS) {
    if (!isSafeCount(value[field])) {
      return `${where}.${field} is ${describe(value[field])}, not a whole number of 0 or more`;
    }
  }
  return null;
}

function checkOpenRiskState(body: JsonRecord): string | null {
  return isMember(OPEN_RISK_STATES, body.open_risk_state)
    ? null
    : `open_risk_state is ${describe(body.open_risk_state)}, not none, bounded or unbounded`;
}

/**
 * `GET /monitoring/overview` (M2-S5): the one response behind the fleet, the
 * ribbon counts and the basket card. Refused as a failed read (`bad_response`,
 * last good answer kept dimmed, never green) when: `rows` is not the deployment
 * rows (M1's `deploymentRowsShape`) with a known `open_risk_state` and
 * `ledger_scope`, a `pending_command` with a finite `requested_at_ms` or null
 * and an object-or-null `entry_barrier`; a group's `deployment_ids` are not
 * integers, its runner counts not whole numbers or its `active` not a boolean;
 * a total names an unknown environment or money owner, or carries `pnl` (no
 * total carries money since M2-S1: the Portfolio book owns it); or a money field
 * the fleet renders (`open_risk`, `allocated_capital`, `pnl.net_pnl`) is not a
 * wire decimal or null.
 */
export const monitoringOverviewShape: ShapeGuard = (value) => {
  if (!isRecord(value)) return `the answer is ${describe(value)}, not an object`;
  if (!isFiniteNumber(value.generated_at_ms)) {
    return `generated_at_ms is ${describe(value.generated_at_ms)}, not a finite number`;
  }
  const rowsFailure = deploymentRowsShape(value.rows);
  if (rowsFailure !== null) return `rows: ${rowsFailure}`;
  const rows = value.rows as unknown[];
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index] as JsonRecord;
    const pending = row.pending_command;
    const failure = firstFailure(
      checkOpenRiskState(row),
      isMember(LEDGER_SCOPES, row.ledger_scope)
        ? null
        : `ledger_scope is ${describe(row.ledger_scope)}, not exchange or paper_untracked`,
      pending === null || (isRecord(pending) && isFiniteNumber(pending.requested_at_ms))
        ? null
        : `pending_command is ${describe(pending)}, not null or a command with a finite requested_at_ms`,
      row.entry_barrier === null || isRecord(row.entry_barrier)
        ? null
        : `entry_barrier is ${describe(row.entry_barrier)}, not an object or null`,
      isWireDecimal(row.open_risk) ? null : `open_risk is ${describe(row.open_risk)}, not a decimal string`,
      checkPnl(row, 'pnl'),
    );
    if (failure !== null) return `rows: row ${index}: ${failure}`;
  }

  if (!Array.isArray(value.groups)) return `groups is ${describe(value.groups)}, not an array`;
  for (let index = 0; index < value.groups.length; index += 1) {
    const group: unknown = value.groups[index];
    if (!isRecord(group)) return `group ${index} is ${describe(group)}, not an object`;
    const ids = group.deployment_ids;
    const failure = firstFailure(
      Array.isArray(ids) && ids.every((id) => typeof id === 'number' && Number.isSafeInteger(id))
        ? null
        : `deployment_ids is ${describe(ids)}, not a list of integers`,
      checkRunnerCounts(group.runner_counts, 'runner_counts'),
      checkOpenRiskState(group),
      checkBoolean(group, 'active'),
      checkMoney(group, 'open_risk'),
      checkMoney(group, 'allocated_capital'),
      checkPnl(group, 'pnl'),
    );
    if (failure !== null) return `group ${index}: ${failure}`;
  }

  if (!Array.isArray(value.totals_by_environment)) {
    return `totals_by_environment is ${describe(value.totals_by_environment)}, not an array`;
  }
  for (let index = 0; index < value.totals_by_environment.length; index += 1) {
    const total: unknown = value.totals_by_environment[index];
    if (!isRecord(total)) return `total ${index} is ${describe(total)}, not an object`;
    const failure = firstFailure(
      isMember(ENVIRONMENTS, total.environment)
        ? null
        : `environment is ${describe(total.environment)}, not paper, testnet or live`,
      isMember(MONEY_SOURCES, total.money_source)
        ? null
        : `money_source is ${describe(total.money_source)}, not portfolio_book or not_measured`,
      total.pnl === null ? null : `pnl is ${describe(total.pnl)}, not null (a total carries no money)`,
      isSafeCount(total.strategy_count)
        ? null
        : `strategy_count is ${describe(total.strategy_count)}, not a whole number of 0 or more`,
      checkRunnerCounts(total.runner_counts, 'runner_counts'),
      checkOpenRiskState(total),
      checkMoney(total, 'open_risk'),
      checkMoney(total, 'allocated_capital'),
    );
    if (failure !== null) return `total ${index}: ${failure}`;
  }
  return checkRunnerCounts(value.runner_counts, 'runner_counts');
};

/**
 * `GET /portfolio/book` (M2-S5): the account book behind Portfolio, the
 * Dashboard and (M2-S6) the Overview ribbon's money. A malformed answer is a
 * failed read on all three: `state` is ok or no_account, `environment` testnet
 * or live, `generated_at_ms` finite, `equity` and `drawdown` objects and
 * `strategies` a list; when ok, `pnl` is an object with a known
 * `unrealized_state` and `net_pnl` / `unrealized_pnl` wire decimals or null
 * (`'0E-12'` is a valid zero) [RV-1].
 */
export const bookOverviewShape: ShapeGuard = (value) => {
  if (!isRecord(value)) return `the answer is ${describe(value)}, not an object`;
  const base = firstFailure(
    isMember(BOOK_STATES, value.state) ? null : `state is ${describe(value.state)}, not ok or no_account`,
    isMember(BOOK_ENVIRONMENTS, value.environment)
      ? null
      : `environment is ${describe(value.environment)}, not testnet or live`,
    isFiniteNumber(value.generated_at_ms)
      ? null
      : `generated_at_ms is ${describe(value.generated_at_ms)}, not a finite number`,
    isRecord(value.equity) ? null : `equity is ${describe(value.equity)}, not an object`,
    isRecord(value.drawdown) ? null : `drawdown is ${describe(value.drawdown)}, not an object`,
    Array.isArray(value.strategies) ? null : `strategies is ${describe(value.strategies)}, not a list`,
  );
  if (base !== null || value.state !== 'ok') return base;
  const pnl = value.pnl;
  if (!isRecord(pnl)) return `pnl is ${describe(pnl)}, not an object, on an ok book`;
  const failure = firstFailure(
    isMember(MARK_STATES, pnl.unrealized_state)
      ? null
      : `unrealized_state is ${describe(pnl.unrealized_state)}, not fresh, stale or unavailable`,
    checkMoney(pnl, 'net_pnl'),
    checkMoney(pnl, 'unrealized_pnl'),
  );
  return failure === null ? null : `pnl.${failure}`;
};

/** `GET /monitoring/coin-side`: the exchange position rows the Positions workspace
 *  groups by environment and Reconciliation › Coin & side lists [M2-S4]. A row
 *  whose freshness or strategy list cannot be read is a failed answer, never a
 *  position filed under the wrong heading or a crash in render. */
export const coinSideRowsShape: ShapeGuard = (value) => {
  if (!Array.isArray(value)) return `the answer is ${describe(value)}, not an array of rows`;
  for (let index = 0; index < value.length; index += 1) {
    const row: unknown = value[index];
    if (!isRecord(row)) return `row ${index} is ${describe(row)}, not an object`;
    const names = row.deployment_names;
    const failure = firstFailure(
      typeof row.exchange_account_id === 'number' && Number.isSafeInteger(row.exchange_account_id)
        ? null
        : `exchange_account_id is ${describe(row.exchange_account_id)}, not an integer`,
      row.environment === null || typeof row.environment === 'string'
        ? null
        : `environment is ${describe(row.environment)}, not a string or null`,
      checkString(row, 'symbol'),
      checkString(row, 'position_side'),
      isMember(SNAPSHOT_STATES, row.snapshot_state)
        ? null
        : `snapshot_state is ${describe(row.snapshot_state)}, not fresh, stale or missing`,
      Array.isArray(names) && names.every((name) => typeof name === 'string')
        ? null
        : `deployment_names is ${describe(names)}, not a list of names`,
    );
    if (failure !== null) return `row ${index}: ${failure}`;
  }
  return null;
};

/** `GET /reconciliation`: the freshness and cleanliness fields the verdict ages. */
export const reconciliationShape: ShapeGuard = (value) => {
  if (!isRecord(value)) return `the answer is ${describe(value)}, not an object`;
  return firstFailure(
    checkBoolean(value, 'initialised'),
    checkBoolean(value, 'stale'),
    checkBoolean(value, 'is_clean'),
    isMember(RECONCILIATION_DATA_STATES, value.data_state)
      ? null
      : `data_state is ${describe(value.data_state)}, not uninitialised, not_run, stale or live`,
    isFiniteNumber(value.staleness_deadline_ms) && value.staleness_deadline_ms > 0
      ? null
      : `staleness_deadline_ms is ${describe(value.staleness_deadline_ms)}, not a positive number`,
    isFiniteNumber(value.generated_at_ms)
      ? null
      : `generated_at_ms is ${describe(value.generated_at_ms)}, not a finite number`,
    value.latest_as_of_ms === null || isFiniteNumber(value.latest_as_of_ms)
      ? null
      : `latest_as_of_ms is ${describe(value.latest_as_of_ms)}, not a finite number or null`,
    isSafeCount(value.open_break_count)
      ? null
      : `open_break_count is ${describe(value.open_break_count)}, not a whole number of 0 or more`,
    isSafeCount(value.unresolved_issue_count)
      ? null
      : `unresolved_issue_count is ${describe(value.unresolved_issue_count)}, not a whole number of 0 or more`,
    Array.isArray(value.tuples) ? null : `tuples is ${describe(value.tuples)}, not an array`,
  );
};

/** `GET /monitoring/engine-city`: telemetry; its `database` block, when sent, is an object. */
export const engineCityTelemetryShape: ShapeGuard = (value) => {
  if (!isRecord(value)) return `the answer is ${describe(value)}, not an object`;
  if (value.database !== undefined && !isRecord(value.database)) {
    return `database is ${describe(value.database)}, not an object`;
  }
  return null;
};

/** `GET /alerts/unacknowledged/count`: whole numbers with `0 <= critical <= count`. */
export const unacknowledgedCountShape: ShapeGuard = (value) => {
  if (!isRecord(value)) return `the answer is ${describe(value)}, not an object`;
  if (!isSafeCount(value.count)) return `count is ${describe(value.count)}, not a whole number of 0 or more`;
  if (!isSafeCount(value.critical)) {
    return `critical is ${describe(value.critical)}, not a whole number of 0 or more`;
  }
  if (value.critical > value.count) return `critical (${value.critical}) exceeds count (${value.count})`;
  return null;
};

/** `GET /deployments/{id}/drift`: a boolean `available`, and an object `payload`
 *  whenever a report is available (the panel reads `payload.state` and its
 *  figures). `{available: true}` with no payload crashed the panel in render. */
export const driftReportShape: ShapeGuard = (value) => {
  if (!isRecord(value)) return `the answer is ${describe(value)}, not an object`;
  const available = checkBoolean(value, 'available');
  if (available !== null) return available;
  if (value.available === true && !isRecord(value.payload)) {
    return `payload is ${describe(value.payload)}, not an object, on an available report`;
  }
  if (value.reason !== undefined && value.reason !== null && typeof value.reason !== 'string') {
    return `reason is ${describe(value.reason)}, not a string or null`;
  }
  return null;
};

/** `GET /system/health`: `kill_switch_tripped` null means unknown, never armed. */
export const healthShape: ShapeGuard = (value) => {
  if (!isRecord(value)) return `the answer is ${describe(value)}, not an object`;
  return firstFailure(
    value.kill_switch_tripped === null || typeof value.kill_switch_tripped === 'boolean'
      ? null
      : `kill_switch_tripped is ${describe(value.kill_switch_tripped)}, not a boolean or null`,
    checkString(value, 'status'),
  );
};
